<script>
import slugify from 'slugify';
import { mapActions } from 'vuex';
import RichTextBlock from '@/components/shared/RichTextBlock.vue';
import SlideNavigation from '@/components/shared/SlideNavigation.vue';
import Tab from './Tab.vue';

export default {
  name: 'ApiSection',

  components: {
    RichTextBlock,
    SlideNavigation,
    Tab,
  },

  props: {
    content: {
      type: Object,
      required: true,
    },
  },

  data() {
    return {
      currentTab: this.content.tabs && this.content.tabs[0],
      isUsingTabs: this.content.tabs && this.content.tabs.length > 1,
      showCurrentTab: true,
    };
  },

  computed: {
    slideNavigationItems() {
      return this.content.tabs.map((tab) => ({
        id: tab.sys.id,
        label: tab.fields.tabLabel,
      }));
    },

    transitionName() {
      const index = this.content.tabs.findIndex((item) => item.sys.id === this.currentTab.sys.id);
      return index === 0 ? 'previous' : 'next';
    },
  },

  methods: {
    ...mapActions('menu', ['setSectionsInView']),

    selectTab(id) {
      this.currentTab = this.content.tabs.find((tab) => tab.sys.id === id);

      // This is for the transition to work
      this.showCurrentTab = false;

      setTimeout(() => {
        this.showCurrentTab = true;
      }, 100);
    },

    getSectionId(heading) {
      return slugify(heading, { lower: true, strict: true });
    },

    visibilityChanged({ isVisible, id }) {
      this.setSectionsInView({ isVisible, id });
    },
  },
};
</script>

<template>
  <div class="section-wrapper" v-if="content">
    <section class="section" :id="getSectionId(content.heading)" v-observe="visibilityChanged">
      <header class="header">
        <div class="endpoint" v-if="content.endpointMethod && content.endpointPath">
          <span class="method">{{ content.endpointMethod }}</span>
          {{ content.endpointPath }}
        </div>

        <h2>{{ content.heading }}</h2>

        <RichTextBlock class="description" v-if="content.text" :data="content.text" />

        <SlideNavigation
          v-if="isUsingTabs"
          :items="slideNavigationItems"
          :currentItemId="currentTab.sys.id"
          @onClick="selectTab"
        />
      </header>

      <div class="tabs">
        <Transition mode="out-in" :name="transitionName">
          <Tab class="tab" v-if="showCurrentTab" :tab="currentTab" />
        </Transition>
      </div>
    </section>
  </div>
</template>

<style lang="scss" scoped>
.section-wrapper {
  @include sections-wrapper;
  max-width: $content-width;
  padding-bottom: 0;

  @include tablet-large {
    padding-bottom: $space-16;
  }

  @include desktop-medium {
    max-width: none;
    padding-bottom: 0;
  }

  :deep(h2) {
    margin-top: 0;
  }
}

.section {
  @include section;

  .header {
    margin-bottom: $space-24;

    @include tablet-large {
      margin-bottom: $space-40;
    }

    .endpoint {
      @include caption-regular;
      margin-bottom: $space-8;

      .method {
        @include inline-code;
        margin-right: $space-16;
        font-weight: 500;
      }
    }

    .description {
      max-width: $code-block-width;
    }
  }
}

.previous-enter-from,
.next-leave-to {
  transform: translateX(-140px);
  opacity: 0;
}

.next-enter-from,
.previous-leave-to {
  transform: translateX(140px);
  opacity: 0;
}

.previous-leave-active,
.next-leave-active,
.previous-enter-active,
.next-enter-active {
  transition: all 0.2s linear;
}
</style>
